// Collapsing a company's many occasions into one row, and the average that makes it worth doing.
//
// A row is one (company, knowable-at) — the endpoint's grain, stated at services/feedView.js. Over a
// multi-year window one company becomes dozens of rows, and "what does this company score over this
// period" is a question the table cannot answer: the analyst reads a column of occasions and averages
// them in their head. This does that arithmetic, over the rows a filter has already narrowed to.
//
// THE ROW THIS PRODUCES IS THE COMPANY'S LATEST OCCASION, with every SCORE on it replaced by a mean.
// That is the whole rule, and everything below follows from it.
//
// "Every score" is both the combined one and each member's own, because they are the same kind of
// thing — a member column is a score spec's value for this company, and averaging the combined score
// while leaving its parts on the latest occasion would put a mean beside the very numbers it was not
// made from. The two now describe the same span. See meanMembers for what that does to a marker.
//
// Everything that is NOT a score — sector, market cap, index membership, the reasoning payload — is
// carried from that one real row rather than summarised, so those cells still name a moment that
// actually happened. The markers stay drillable: a member's identity comes from its newest mention,
// so the target is still a feed the response named.
//
// What CANNOT be carried is the pair the previous-occasion columns read. `prevScore` describes the
// occasion before a specific one; an average has no "before". They are nulled here rather than left
// to render whatever the latest row happened to hold, which would put a real number under a heading
// that no longer means anything.
//
// NULLS ARE NOT ZEROS. The mean is taken over the scored occasions only, and a company whose every
// occasion is unscored stays null. docs/FEED_VIEW_PLAN.md names this as the failure mode to avoid:
// "any sort/aggregate treating null as 0 corrupts the exact signal this page exists to show." A
// company scored 4 in one of three occasions averages 4 — not 1.33.

// normalizeRow has already run every score through its own finite-or-null guard, so this is a belt
// on top of braces. Kept local rather than exported from services/feedView.js: widening the
// normalizer's public surface for a one-line numeric guard is the larger change of the two.
const asFiniteNumber = (value) => (typeof value === 'number' && isFinite(value) ? value : null);

const instantOf = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return isNaN(parsed) ? null : parsed;
};

// The group's latest occasion, by instant, falling back to the last row in server order — which is
// also the tie-break attachPreviousScores uses when two rows share an instant. A row with an
// unparseable stamp can never win on time, but it can still be the answer when nothing in the group
// has a readable one; picking the last is arbitrary and deliberately deterministic.
const latestOf = (entries) => {
  let best = null;
  entries.forEach((entry) => {
    const instant = instantOf(entry.row.knowableAt);
    if (instant === null) return;
    if (best === null || instant >= best.instant) best = { instant, row: entry.row };
  });
  return best ? best.row : entries[entries.length - 1].row;
};

// Earliest and latest stamps the group actually covers, for the cell that has to say what was folded
// together. Null when not one occasion carries a readable stamp.
const spanOf = (entries) => {
  const instants = entries.map((entry) => instantOf(entry.row.knowableAt)).filter((v) => v !== null);
  if (!instants.length) return null;
  const rows = entries.filter((entry) => instantOf(entry.row.knowableAt) !== null);
  const sorted = rows.slice().sort((a, b) => instantOf(a.row.knowableAt) - instantOf(b.row.knowableAt));
  return { from: sorted[0].row.knowableAt, to: sorted[sorted.length - 1].row.knowableAt };
};

// The mean of the scored occasions. Null — never 0 — when none of them scored.
const meanScore = (entries) => {
  const scores = entries.map((entry) => asFiniteNumber(entry.row.score)).filter((score) => score !== null);
  if (!scores.length) return { score: null, scoredOccurrences: 0 };
  const total = scores.reduce((sum, score) => sum + score, 0);
  return { score: total / scores.length, scoredOccurrences: scores.length };
};

// The member columns get the same treatment as the score, because they are the same KIND of thing:
// each one is a score spec's own value for this company, and averaging the combined score while
// leaving its parts on the latest occasion would put a mean beside the numbers it was not made from.
//
// A member's STATUS cannot be averaged, so it is derived across the occasions that contributed:
//
//   every contributor fresh            -> present
//   any contributor carried forward    -> stale, and the dot now reads "at least one of these was
//                                         carried forward" rather than "this one was"
//   no occasion had a value at all     -> missing
//
// The per-member counts ride along as `occasionsWithValue` / `staleOccasions` so the cell can say
// exactly what it did instead of leaving the derived word to imply it.
//
// `srcKnowableAt` is dropped: it names the instant ONE value was carried from, and an average has no
// single such instant. Leaving the latest one would answer a question about the mean with a fact
// about one of its terms.
const meanMembers = (entries) => {
  const order = [];
  const byKey = new Map();

  entries.forEach((entry) => {
    (entry.row.members || []).forEach((input) => {
      const key = String(input.key);
      if (!byKey.has(key)) {
        byKey.set(key, []);
        order.push(key);
      }
      byKey.get(key).push(input);
    });
  });

  return order.map((key) => {
    const seen = byKey.get(key);
    // The newest mention wins for the member's identity — label and feed id — so the column heading
    // and the drill-down target stay what the latest occasion called them.
    const identity = seen[seen.length - 1];
    const values = seen.map((input) => asFiniteNumber(input.value)).filter((value) => value !== null);
    const contributors = seen.filter((input) => asFiniteNumber(input.value) !== null);
    const staleOccasions = contributors.filter((input) => input.status === 'stale').length;

    const status = values.length === 0 ? 'missing' : staleOccasions > 0 ? 'stale' : 'present';

    return {
      ...identity,
      key,
      status,
      value: values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null,
      srcKnowableAt: null,
      aggregated: true,
      occasions: seen.length,
      occasionsWithValue: values.length,
      staleOccasions,
    };
  });
};

// One row per company, in the order each company first appears — so the result of aggregating is
// stable, and a sort applied afterwards is sorting something deterministic.
//
// A row with no company_id is emitted UNCHANGED and never merged with another. It has no identity to
// group by, and pooling the id-less rows together would invent a company out of unrelated ones — the
// rule attachPreviousScores states for predecessors and rowFilters states for its label index. Such a
// row keeps its own key and carries no occurrence count, because it summarises nothing.
export const aggregateByCompany = (rows) => {
  const list = rows || [];

  const groups = new Map();
  const order = [];
  const loose = [];

  list.forEach((row, index) => {
    if (row?.companyId === null || row?.companyId === undefined) {
      loose.push({ row, index });
      return;
    }
    // Keyed by String so a response spelling one company's id as a number and another's as a string
    // still groups each with itself — the same normalization attachPreviousScores applies.
    const company = String(row.companyId);
    if (!groups.has(company)) {
      groups.set(company, []);
      order.push({ company, index });
    }
    groups.get(company).push({ row, index });
  });

  const aggregated = order.map(({ company, index }) => {
    const entries = groups.get(company);
    const latest = latestOf(entries);
    const { score, scoredOccurrences } = meanScore(entries);
    return {
      ...latest,
      // Keyed on the company rather than on the latest row, so the identity of an aggregated row does
      // not change when a filter moves which occasion is newest.
      key: `aggregate-${company}`,
      score,
      // Averaged alongside the score, not carried from the latest row — see meanMembers.
      members: meanMembers(entries),
      // An average has no previous occasion. Nulled rather than inherited — see the header.
      prevScore: null,
      prevScoreKnowableAt: null,
      scoreDelta: null,
      aggregated: true,
      occurrences: entries.length,
      scoredOccurrences,
      aggregatedSpan: spanOf(entries),
      sortIndex: index,
    };
  });

  const passthrough = loose.map(({ row, index }) => ({ ...row, sortIndex: index }));

  return aggregated
    .concat(passthrough)
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map(({ sortIndex, ...row }) => row);
};

// Whether aggregating would actually change what is on screen. Offered only when some company holds
// more than one occasion — with one row each, the toggle reproduces the table it sits above, and a
// control that cannot change anything is worse than no control (the rule TableControls already
// applies to its selects and its window chip).
export const canAggregate = (rows) => {
  const seen = new Set();
  return (rows || []).some((row) => {
    if (row?.companyId === null || row?.companyId === undefined) return false;
    const company = String(row.companyId);
    if (seen.has(company)) return true;
    seen.add(company);
    return false;
  });
};

// What the count line says it did. `occurrences` counts only the rows that were folded — an id-less
// passthrough row summarises nothing and is not one of them.
export const aggregateSummary = (rows) => {
  const counted = (rows || []).filter((row) => row?.aggregated);
  return {
    companies: (rows || []).length,
    occurrences: counted.reduce((sum, row) => sum + (row.occurrences || 0), 0),
  };
};
